import React from 'react';
import { Search, Shield, BarChart2, Sparkles, ChevronRight, Award, CheckCircle, TrendingUp, Clock, Users, Zap } from 'lucide-react';

const CtaSectionComparison: React.FC = () => {
  const company = "Silver International SA";

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Comparaison des 3 Options de Section CTA</h1>
            <p className="text-gray-600 mt-2">Alternatives professionnelles à la section orange "Envie d'aller plus loin"</p>
          </div>
        </div>
      </header>

      <div className="py-12 space-y-16">
        
        {/* Option 1: Minimalisme Corporate */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Option 1: Corporate Minimaliste</h2>
              <p className="text-gray-600">Design épuré avec fond blanc et éléments subtils</p>
            </div>
            
            {/* CTA Section - Style Corporate Minimaliste */}
            <section className="relative bg-white py-16 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="bg-gray-50 rounded-2xl p-12 text-center border border-gray-200">
                    
                    {/* Company badge */}
                    <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 bg-white rounded-full border border-gray-200 shadow-sm">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                        <BarChart2 className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{company}</span>
                    </div>
                    
                    {/* Title */}
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      Prêt à optimiser votre présence digitale ?
                    </h2>
                    
                    {/* Subtitle */}
                    <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                      Répondez à quelques questions pour obtenir votre diagnostic personnalisé
                    </p>
                    
                    {/* Features */}
                    <div className="flex flex-wrap justify-center gap-6 mb-10">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-5 h-5" />
                        <span className="text-sm">5 minutes</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Shield className="w-5 h-5" />
                        <span className="text-sm">100% sécurisé</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <TrendingUp className="w-5 h-5" />
                        <span className="text-sm">Résultats instantanés</span>
                      </div>
                    </div>
                    
                    {/* CTA Button */}
                    <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors shadow-lg hover:shadow-xl">
                      Commencer l'analyse
                    </button>
                    
                    {/* Trust line */}
                    <p className="text-sm text-gray-500 mt-6">
                      ✓ Sans engagement • ✓ Données protégées • ✓ Recommandations personnalisées
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Option 2: Glassmorphism Moderne */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Option 2: Glassmorphism Moderne</h2>
              <p className="text-gray-600">Design moderne avec effets de transparence et profondeur</p>
            </div>
            
            {/* CTA Section - Glassmorphism */}
            <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-16 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ddd6fe%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
              
              {/* Floating elements */}
              <div className="absolute top-10 left-1/4 w-20 h-20 bg-blue-400/10 rounded-full blur-xl"></div>
              <div className="absolute bottom-10 right-1/4 w-32 h-32 bg-indigo-400/10 rounded-full blur-xl"></div>
              
              <div className="container mx-auto px-4 relative">
                <div className="max-w-4xl mx-auto">
                  <div className="backdrop-blur-sm bg-white/70 rounded-3xl p-12 text-center border border-white/30 shadow-2xl">
                    
                    {/* Icon with glow */}
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-6 shadow-lg">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Company name */}
                    <div className="inline-block px-4 py-1 bg-blue-100/80 rounded-full text-blue-700 font-medium text-sm mb-6">
                      {company}
                    </div>
                    
                    {/* Title */}
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                      Découvrez votre potentiel digital
                    </h2>
                    
                    {/* Subtitle */}
                    <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                      Une analyse approfondie pour révéler les opportunités cachées de votre site web
                    </p>
                    
                    {/* Feature cards */}
                    <div className="grid md:grid-cols-3 gap-4 mb-10">
                      <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                        <Search className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-700">Analyse SEO</p>
                      </div>
                      <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                        <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-700">Sécurisé</p>
                      </div>
                      <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                        <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-700">Instantané</p>
                      </div>
                    </div>
                    
                    {/* CTA Button */}
                    <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-10 py-4 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105">
                      <span className="flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Obtenir mon diagnostic
                        <ChevronRight className="w-5 h-5" />
                      </span>
                    </button>
                    
                    {/* Trust indicators */}
                    <div className="flex justify-center items-center mt-8 space-x-6 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Gratuit
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        Confidentiel
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        Personnalisé
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Option 3: Tech Dashboard Style */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Option 3: Tech Dashboard (Recommandée)</h2>
              <p className="text-gray-600">Style tech moderne avec preview des métriques et interface dashboard</p>
            </div>
            
            {/* CTA Section - Tech Dashboard */}
            <section className="relative bg-gray-50 py-16 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    
                    {/* Left side - Content */}
                    <div>
                      {/* Status badge */}
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full text-blue-700 font-medium text-sm mb-6">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        Analyse en temps réel
                      </div>
                      
                      {/* Title */}
                      <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                        Optimisez les performances de 
                        <span className="text-blue-600"> {company}</span>
                      </h2>
                      
                      {/* Subtitle */}
                      <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                        Diagnostic complet avec métriques détaillées et plan d'action personnalisé pour booster votre visibilité en ligne.
                      </p>
                      
                      {/* Features list */}
                      <div className="space-y-4 mb-8">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-gray-700">Analyse SEO technique complète</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-gray-700">Audit de performance et vitesse</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-gray-700">Recommandations IA personnalisées</span>
                        </div>
                      </div>
                      
                      {/* CTA Button */}
                      <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105 mb-4">
                        <span className="flex items-center gap-2">
                          <BarChart2 className="w-5 h-5" />
                          Lancer l'analyse complète
                          <ChevronRight className="w-5 h-5" />
                        </span>
                      </button>
                      
                      {/* Social proof */}
                      <div className="flex items-center gap-3 text-gray-600">
                        <div className="flex -space-x-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-xs font-semibold text-blue-600">A</span>
                          </div>
                          <div className="w-8 h-8 bg-green-100 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-xs font-semibold text-green-600">B</span>
                          </div>
                          <div className="w-8 h-8 bg-purple-100 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-xs font-semibold text-purple-600">C</span>
                          </div>
                        </div>
                        <span className="text-sm">+400 entreprises analysées ce mois</span>
                      </div>
                    </div>
                    
                    {/* Right side - Dashboard Preview */}
                    <div>
                      <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-semibold text-gray-900">{company}</span>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Live</span>
                        </div>
                        
                        {/* Metrics */}
                        <div className="space-y-6">
                          {/* SEO Score */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-600">Score SEO</span>
                              <span className="text-lg font-bold text-gray-900">75%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-1000" style={{width: '75%'}}></div>
                            </div>
                          </div>
                          
                          {/* Performance */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-600">Performance</span>
                              <span className="text-lg font-bold text-gray-900">82%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-1000" style={{width: '82%'}}></div>
                            </div>
                          </div>
                          
                          {/* UX Score */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-600">Expérience utilisateur</span>
                              <span className="text-lg font-bold text-gray-900">68%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-3 rounded-full transition-all duration-1000" style={{width: '68%'}}></div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Quick stats */}
                        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-100">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">127</div>
                            <div className="text-xs text-gray-500">Mots-clés</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">94%</div>
                            <div className="text-xs text-gray-500">Uptime</div>
                          </div>
                        </div>
                        
                        {/* Footer */}
                        <div className="mt-6 pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Award className="w-4 h-4" />
                            <span>Dernière analyse: il y a 2 minutes</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Comparison Guide */}
        <div className="bg-gray-900 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h3 className="text-3xl font-bold mb-6">Quelle option correspond à votre entreprise ?</h3>
            <p className="text-gray-300 mb-12 text-lg max-w-3xl mx-auto">
              Chaque style convient à différents types d'entreprises et secteurs d'activité
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold mb-4">Option 1: Corporate</h4>
                <p className="text-gray-300 text-sm mb-4">
                  Idéal pour les entreprises traditionnelles, cabinets de conseil, services B2B établis
                </p>
                <div className="text-xs text-gray-400">
                  ✓ Professionnel • ✓ Sobre • ✓ Rassurant
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold mb-4">Option 2: Glassmorphism</h4>
                <p className="text-gray-300 text-sm mb-4">
                  Parfait pour les startups, agences créatives, entreprises innovantes
                </p>
                <div className="text-xs text-gray-400">
                  ✓ Moderne • ✓ Créatif • ✓ Différenciant
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-xl p-8 border-2 border-blue-500">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <BarChart2 className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold mb-4">Option 3: Tech Dashboard ⭐</h4>
                <p className="text-gray-300 text-sm mb-4">
                  Recommandé pour SaaS, tech, services digitaux, data-driven
                </p>
                <div className="text-xs text-gray-400">
                  ✓ Données • ✓ Performance • ✓ Crédibilité
                </div>
              </div>
            </div>
            
            <div className="mt-12 p-6 bg-blue-900/50 rounded-xl border border-blue-700 max-w-2xl mx-auto">
              <h5 className="font-semibold mb-2">💡 Notre recommandation</h5>
              <p className="text-sm text-blue-100">
                L'Option 3 (Tech Dashboard) est idéale pour votre secteur car elle met en avant les données et performances, 
                créant immédiatement la confiance grâce à la preview des métriques.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CtaSectionComparison;
